import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/navigation';
import axios from 'axios';
import { logout } from '../../app/redux/slices/authSlice';

// Custom hook for API calls with Redux dispatch

const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const router = useRouter();

  // APIObject: { endpoint, method, successActionType, failureActionType }
  // params: request body or query params
  const makeApiCall = useCallback(
    async (apiObject, params = null) => {
      const {
        endpoint,
        params: configParams,
        successActionType,
        failureActionType,
        headers,
      } = apiObject;
      const method = endpoint.method || 'get';
      const url = endpoint.url;

      console.log('useApiCall: Making request', {
        method,
        url,
        data: method === 'get' ? undefined : params || configParams,
        params: method === 'get' ? params || configParams : undefined,
        headers,
      });

      setLoading(true);
      setError(null);
      try {
        const axiosConfig = {
          method,
          url,
          headers,
        };

        // For GET requests, use params (query string), for others use data (body)
        if (method === 'get') {
          // buildApiConfig already appends query string to URL for GET requests
          // So we don't need to pass params again
          // axiosConfig.params = params || configParams;
        } else {
          axiosConfig.data = params || configParams;
        }

        const response = await axios(axiosConfig);

        console.log('useApiCall: Response received', {
          url,
          status: response.status,
          data: response.data,
        });

        if (successActionType) {
          dispatch({ type: successActionType, payload: response.data });
        }
        return response.data;
      } catch (err) {
        console.error('useApiCall: Request failed', {
          url,
          error: err.response?.data || err.message,
          status: err.response?.status,
        });

        // Handle 403 Forbidden - Session expired
        if (err.response?.status === 403) {
          console.log('useApiCall: 403 detected - Session expired, logging out');

          // Clear auth state
          dispatch(logout());
          localStorage.removeItem('auth');

          // Show toast notification
          try {
            const { emit } = require('@/utils/eventBus');
            emit('session-expired', {
              message: 'Your session has expired. Please login again.',
            });
          } catch (err) {
            console.error('[useApiCall] eventBus emit failed:', err);
          }

          // Redirect to login after a short delay
          setTimeout(() => {
            router.push('/login');
          }, 1500);
        }

        setError(err);
        if (failureActionType) {
          dispatch({ type: failureActionType, payload: err });
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, router]
  );

  return { loading, error, makeApiCall };
};

export default useApiCall;
